import axios from 'axios'
import laroute from '~/laroute'
import store from '~/store'
import moment from 'moment'
import { Invoice } from '~/_shared/models'

// State
export const state = {
  invoices: [],
  invoice: new Invoice(),
  total: 0,
  sales_orders: [],
  packing_slips: [],
  sales_orders_for_prepaid: [],
  sales_orders_for_so: [],
  withdraw_amount: 0,
  invoice_id: 0,
  pdf: undefined
}

// Getters
export const getters = {
  invoices: state => state.invoices,
  invoice: state => state.invoice,
  total: state => state.total,
  sales_orders: state => state.sales_orders,
  packing_slips: state => state.packing_slips,
  sales_orders_for_prepaid: state => state.sales_orders_for_prepaid,
  sales_orders_for_so: state => state.sales_orders_for_so,
  withdraw_amount: state => state.withdraw_amount,
  invoice_id: state => state.invoice_id,
  pdf: state => state.pdf
}

// Mutations
export const mutations = {
  INVOICES (state, data) {
    state.invoices = data
  },

  INVOICE (state, data) {
    state.invoice = Object.assign(new Invoice(), data)
  },

  TOTAL (state, data) {
    state.total = data
  },

  SALES_ORDERS (state, data) {
    state.sales_orders = data
  },

  PACKING_SLIPS (state, data) {
    state.packing_slips = data
  },

  SALES_ORDERS_FOR_PREPAID (state, data) {
    state.sales_orders_for_prepaid = data
  },

  SALES_ORDERS_FOR_SO (state, data) {
    state.sales_orders_for_so = data
  },

  WITHDRAW_AMOUNT (state, data) {
    state.withdraw_amount = data
  },

  INVOICE_ID (state, data) {
    state.invoice_id = data
  },

  PDF (state, data) {
    state.pdf = data
  }
}

// Actions
export const actions = {
  async fetchAll ({ commit }, filterData) {
    await axios.get(
      laroute.route('invoices_filter', { companyAbbr: store.getters['auth/company'], ...filterData })
    ).then(res => {
      res.data.data.items.map((invoice) => {
        invoice.created_at = moment(invoice.created_at).format('YYYY-MM-DD')
        invoice.due_date = moment(invoice.due_date).format('YYYY-MM-DD')
      })

      commit('INVOICES', res.data.data.items)
      commit('TOTAL', res.data.data.total)
    }).catch(err => {
      console.log(err)
    })
  },

  async fetchOne ({ commit }, id) {
    await axios.get(laroute.route('get_invoice', {
      id
    })).then(res => {
      let result = res.data.data.data.invoice
      commit('INVOICE', result)
    }).catch(err => {
      throw err
    })
  },

  async fetchOneByOrderNo ({ commit }, orderNo) {
    await axios.get(laroute.route('get_invoice_by_order_no', {
      orderNo
    })).then(res => {
      let result = res.data.data.data.invoice
      commit('INVOICE', result)
    }).catch(err => {
      throw err
    })
  },

  async createFromSO ({ commit }, data) {
    await axios.post(laroute.route('create_invoice_so', { companyAbbr: store.getters['auth/company'] }), data)
      .then(res => {
        commit('INVOICE', res.data.data.data)
      })
      .catch(err => {
        console.log(err)
        throw err
      })
  },

  async createFromBlank ({ commit }, data) {
    await axios.post(laroute.route('create_invoice_blank', { companyAbbr: store.getters['auth/company'] }), data)
      .then(res => {
        commit('INVOICE', res.data.data.data)
      })
      .catch(err => {
        console.log(err)
        throw err
      })
  },

  async createFromPrepaid ({ commit }, data) {
    await axios.post(laroute.route('create_invoice_prepaid', { companyAbbr: store.getters['auth/company'] }), data)
      .then(res => {
        commit('INVOICE', res.data.data.data)
      })
      .catch(err => {
        console.log(err)
        throw err
      })
  },

  async createItems ({ commit }, { id, items }) {
    await axios.patch(laroute.route('add_item_invoice', { id }), { items })
  },

  async update ({ commit }, { id, invoice, items, appliedPrepaidAmount }) {
    await axios.patch(laroute.route('update_invoice', { id }), { invoice, items, appliedPrepaidAmount })
  },

  async localInvoiceUpdate ({ commit }, invoice) {
    console.log('actions', invoice)
    commit('INVOICE', invoice)
  },

  async delete ({ commit }, { id, reason }) {
    await axios.delete(laroute.route('delete_invoice', { id }), { data: { reason } })
  },

  async deleteItem ({ commit }, { id }) {
    await axios.delete(laroute.route('delete_invoice_item', { id }))
  },

  async getSalesOrders ({ commit }) {
    await axios.get(laroute.route('get_sales_orders', { companyAbbr: store.getters['auth/company'] }))
      .then(res => {
        commit('SALES_ORDERS', res.data)
      })
  },

  async getSalesOrdersForSo ({ commit }) {
    await axios.get(laroute.route('get_sales_orders_for_so', { companyAbbr: store.getters['auth/company'] }))
      .then(res => {
        commit('SALES_ORDERS_FOR_SO', res.data)
      })
  },

  async getPackingSlips ({ commit }, data) {
    await axios.get(laroute.route('get_sales_order_packing_slips', { id: data.salesOrderId, companyAbbr: store.getters['auth/company'] }))
      .then(res => {
        commit('PACKING_SLIPS', res.data)
      })
  },

  async getSalesOrdersForPrepaid ({ commit }) {
    await axios.get(laroute.route('get_sales_orders_for_prepaid', { companyAbbr: store.getters['auth/company'] }))
      .then(res => {
        commit('SALES_ORDERS_FOR_PREPAID', res.data)
      })
  },

  async getWithdrawAmount ({ commit }, id) {
    await axios.get(laroute.route('get-prepaid-invoice-withdraw-amount', { id }))
      .then(res => {
        commit('WITHDRAW_AMOUNT', res.data.amount)
        commit('INVOICE_ID', res.data.invoice_id)
      })
  },

  async updateInvoiceCreditTotal ({ commit }, { prepaidInvoiceId, appliedPrepaidAmount }) {
    await axios.patch(laroute.route('update-applied-credit-total', { id: prepaidInvoiceId }), { total: appliedPrepaidAmount })
  },

  async getCreditMemoPdf ({ commit }, { id }) {
    const { data } = await axios.post(laroute.route('credit_memo_pdf', { companyAbbr: store.getters['auth/company'], id: id }), {}, { responseType: 'arraybuffer' })
    commit('PDF', data)
  }
}



// WEBPACK FOOTER //
// ./resources/assets/js/store/modules/invoice.js